/**
 * Single source of truth for a ControlID board's event-push ("modo monitor") destination.
 *
 * A ControlID board POSTs events only to the destination stored in its `monitor` configuration.
 * Without one it stays online, answers every request and syncs people normally — while sending
 * no events at all. That is indistinguishable, from the UI, from a device nobody has walked
 * through yet.
 *
 * Both the writer ("Configurar") and the reader ("Ler configuração") derive from here, so they
 * cannot drift apart — the Hikvision panel had a configurator writing HTTP:80 and a validator
 * expecting HTTPS:443, which made every device report "Configuração incorreta" forever.
 */

export const MONITOR_HOST = 'access.panther.com.br';

/**
 * Transport the monitor is configured with.
 *
 * Port 80 is what "Configurar" has always written, so it is what every board in the field is
 * running and what events actually arrive over. Displaying an https:// URL beside a monitor
 * written as port 80 is precisely the lie the Hikvision panel used to tell. Moving to TLS is a
 * one-line change here plus a deliberate re-push to every board.
 */
// Typed as string, not narrowed to the literal '80': this is a knob meant to be changed, and
// every consumer must keep compiling when it becomes '443'.
export const MONITOR_PORT: string = '80';
export const MONITOR_REQUEST_TIMEOUT = '5000';

export interface ControlIdMonitorConfig {
  hostname: string;
  port: string;
  path: string;
  request_timeout?: string;
}

export interface ControlIdMonitorComparison {
  /** The device has SOME monitor set — distinct from matching ours. */
  configured: boolean;
  ok: boolean;
  hostnameOk: boolean;
  portOk: boolean;
  pathOk: boolean;
  actual: ControlIdMonitorConfig | null;
  expected: ControlIdMonitorConfig;
}

/**
 * ControlID stores the path without a leading slash. Compare normalized so the same destination
 * written either way is recognised as the same — otherwise a correctly configured board reports
 * as wrong forever.
 */
const normalizePath = (path: string): string => (path || '').trim().replace(/^\/+/, '').replace(/\/+$/, '');

export function expectedControlIdMonitor(deviceId: string): ControlIdMonitorConfig {
  return {
    hostname: MONITOR_HOST,
    port: MONITOR_PORT,
    path: `api/v1/iot-connect/facial-recognition/events/controlid/${deviceId}`,
    request_timeout: MONITOR_REQUEST_TIMEOUT,
  };
}

/** The URL shown in the panel. Derived from the expected monitor by construction. */
export function controlIdWebhookUrl(deviceId: string): string {
  const { hostname, port, path } = expectedControlIdMonitor(deviceId);
  const scheme = port === '443' ? 'https' : 'http';
  const isDefaultPort = port === '443' || port === '80';
  const authority = isDefaultPort ? hostname : `${hostname}:${port}`;
  return `${scheme}://${authority}/${normalizePath(path)}`;
}

export function compareControlIdMonitor(
  actual: Partial<ControlIdMonitorConfig> | null | undefined,
  deviceId: string,
): ControlIdMonitorComparison {
  const expected = expectedControlIdMonitor(deviceId);

  const hostname = (actual?.hostname || '').trim();
  const port = String(actual?.port ?? '').trim();
  const path = normalizePath(actual?.path || '');

  // Nothing set at all. Reporting this as a "mismatch" would send the operator looking for a
  // misconfiguration when the real answer is that the board was never pointed anywhere.
  const configured = !!(hostname || path);
  if (!configured) {
    return {
      configured: false,
      ok: false,
      hostnameOk: false,
      portOk: false,
      pathOk: false,
      actual: null,
      expected,
    };
  }

  const hostnameOk = hostname.toLowerCase() === expected.hostname.toLowerCase();
  const portOk = port === String(expected.port);
  const pathOk = path === normalizePath(expected.path);

  return {
    configured: true,
    ok: hostnameOk && portOk && pathOk,
    hostnameOk,
    portOk,
    pathOk,
    actual: { hostname, port, path, request_timeout: actual?.request_timeout },
    expected,
  };
}
